<template>
    <div class="apprentice-simple-card remove-bottom-card-margin">
        <design-2021-apprentice-data-table
            class="files-table"
            v-if="searchedFiles.length"
            :items="searchedFiles"
            :fields="fileFields">
            <template v-slot:options>
                <b-button
                    class="btn-upload btn-upload-file"
                    v-b-modal="'upload-file-modal'"
                    variant="primary">
                    {{ $t('group.add_file') }}
                </b-button>
            </template>

            <template v-slot:cell(visibility)="data">
                <div class="visibility-status">
                    <i
                        class="fad text-primary"
                        v-b-tooltip.hover
                        :class="data.item.visibility ? 'fa-user-lock' : 'fa-users'"
                        :title="data.item.visibility ? $t('group.only_for_teachers') : $t('group.for_everyone')">
                    </i>
                </div>
            </template>
            <template v-slot:cell(actions)="data">
                <div class="file-actions">
                    <b-button
                        class="btn-edit btn-file-edit"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('common.edit')"
                        @click="editFile(data.item)">
                    </b-button>
                    <b-button
                        class="btn-download btn-file-download"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('common.download')"
                        :href="data.item.public_path"
                        target="_blank">
                    </b-button>

                    <b-button
                        class="btn-remove btn-remove-file"
                        v-b-tooltip.hover
                        variant="danger-icon-only"
                        :title="$t('common.delete')"
                        @click="onRemove(data.item.id)">
                    </b-button>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <div
            class="not-found with-header empty-group-show-files-list"
            v-else>
            <div
                class="btn-add-circle icon"
                v-b-modal="'upload-file-modal'"
                v-b-tooltip.hover
                :title="$t('group.add_file')"></div>
            <div class="alert-notice">{{ $t('group.no_file_uploaded') }}</div>
        </div>

        <design-2021-group-show-upload-file-modal-component
            :group-id="groupId"
            @file-uploaded="addFile($event)">
        </design-2021-group-show-upload-file-modal-component>
        <design-2021-group-show-file-edit-modal-component
            ref="showEditFileModal"
            @file-update="updateFile">
        </design-2021-group-show-file-edit-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-group-file-component',
    props: ['groupId', 'filesIn'],
    data() {
        return {
            searchTerm: null,
            files: this.filesIn.map((file) => {
                let uploadedBy = file.user ? file.user.full_name : 'verwijderde gebruiker';
                return {
                    id: file.id,
                    name: file.full_name,
                    description: file.pivot.description,
                    uploadedBy: uploadedBy,
                    public_path: file.public_path,
                    visibility: file.pivot.teacher_only,
                };
            }),
            fileFields: [
                {
                    key: 'name',
                    label: this.$t('group.files'),
                    sortable: true,
                },
                {
                    key: 'description',
                    label: this.$t('common.description'),
                    sortable: true,
                },
                {
                    key: 'uploadedBy',
                    label: this.$t('group.uploaded_by'),
                },
                {
                    key: 'visibility',
                    label: this.$t('group.available'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
        };
    },
    methods: {
        addFile(file) {
            this.files.push({
                id: file.id,
                name: file.full_name,
                description: file.pivot.description,
                uploadedBy: file.user.full_name,
                public_path: file.public_path,
                visibility: file.pivot.teacher_only,
            });
        },
        onRemove(id) {
            this.alert
                .confirm(
                    this.$t('group.delete_file_confirmation_title'),
                    this.$t('group.are_you_sure_you_want_to_delete_this_body'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();
                        this.axios.delete(this.routes.groupFileRemove(this.groupId, id)).then(
                            (response) => {
                                this.files = this.files.filter((item) => item.id !== id);
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        editFile(fileRow) {
            const selectedFile = this.files.filter((file) => file.id === fileRow.id);
            if (selectedFile) {
                this.$refs.showEditFileModal.show(fileRow);
            }
        },

        updateFile(updatedFile) {
            this.alert
                .confirm(
                    this.$t('group.update_file_confirmation_title'),
                    this.$t('group.update_file_confirmation_body'),
                    this.$t('common.yes_update'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.post(this.routes.groupFileEdit(this.groupId, updatedFile.id), updatedFile).then(
                        (response) => {
                            this.alert.success();
                            this.files = this.files.map((file) => {
                                if (file.id === updatedFile.id) {
                                    file.name = updatedFile.name;
                                    file.description = updatedFile.description;
                                    file.visibility = updatedFile.visibility;
                                }

                                return file;
                            });
                        },

                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
    },
    computed: {
        searchedFiles() {
            if (this.searchTerm === null || this.searchTerm == '') return this.files;

            return this.files.filter((file) => {
                this.searchTerm = this.searchTerm.toLowerCase();
                if (
                    file.name.toLowerCase().indexOf(this.searchTerm) > -1 ||
                    file.description.toLowerCase().indexOf(this.searchTerm) > -1 ||
                    file.uploadedBy.toLowerCase().indexOf(this.searchTerm) > -1
                ) {
                    return file;
                }
            });
        },
    },
};
</script>

<style scoped lang="scss">
.file-actions {
    display: flex;
    align-items: center;

    .btn:not(:last-child) {
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
    }

    .btn-file-download:before {
        margin-right: 0;
    }
}

::v-deep .files-table {
    .apprentice-table-header {
        .apprentice-data-table-actions {
            margin-top: map-get($grid-gutter-spacing, 'ten');
        }
    }
}

.empty-group-show-files-list {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.visibility-status {
    font-size: 1.3125rem;
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    ::v-deep .files-table {
        .apprentice-table-header {
            flex-direction: row;

            .apprentice-table-filters,
            .apprentice-data-table-actions {
                flex: 0 0 auto;
            }

            .apprentice-data-table-actions {
                margin-top: 0;
            }
        }
    }
}
</style>
